import { z } from 'zod/v3';

export const PageContextSchema = z.object({
  page: z.string(),
  summary: z.string(),
  data: z.record(z.unknown()).optional(),
});

export type PageContext = z.infer<typeof PageContextSchema>;

export const PageSnapshotSchema = z.object({
  content: z.string(),
  path: z.string().optional(),
  capturedAt: z.string().optional(),
  contentHash: z.string().optional(),
});

export type PageSnapshot = z.infer<typeof PageSnapshotSchema>;

export const SlackTurnContextSchema = z.object({
  channelId: z.string(),
  channelName: z.string().optional(),
  threadTs: z.string().optional(),
});

export type SlackTurnContext = z.infer<typeof SlackTurnContextSchema>;

export const TurnContextSchema = z.object({
  programId: z.string().optional(),
  pageContext: PageContextSchema.optional(),
  pageSnapshot: PageSnapshotSchema.optional(),
  slackContext: SlackTurnContextSchema.optional(),
});

export type TurnContext = z.infer<typeof TurnContextSchema>;

/**
 * Merge two TurnContext objects. Shallow merge at top level.
 * pageContext.data is additive (update spread over base).
 * page/summary replaced by update if present.
 * pageSnapshot replaced wholesale by update if present.
 */
export function mergeTurnContext(
  base: TurnContext | undefined,
  update: Partial<TurnContext> | undefined,
): TurnContext {
  if (!base && !update) return {};
  if (!base) return { ...update } as TurnContext;
  if (!update) return { ...base };

  const mergedPageContext =
    base.pageContext || update.pageContext
      ? {
          page: update.pageContext?.page ?? base.pageContext?.page ?? 'unknown',
          summary: update.pageContext?.summary ?? base.pageContext?.summary ?? '',
          ...(base.pageContext?.data || update.pageContext?.data
            ? {
                data: {
                  ...(base.pageContext?.data ?? {}),
                  ...(update.pageContext?.data ?? {}),
                },
              }
            : {}),
        }
      : undefined;

  return {
    programId: update.programId ?? base.programId,
    ...(mergedPageContext ? { pageContext: mergedPageContext } : {}),
    ...(update.pageSnapshot
      ? { pageSnapshot: update.pageSnapshot }
      : base.pageSnapshot
        ? { pageSnapshot: base.pageSnapshot }
        : {}),
    ...(update.slackContext
      ? { slackContext: update.slackContext }
      : base.slackContext
        ? { slackContext: base.slackContext }
        : {}),
  };
}
